import { useEffect, useState } from 'react'

import { api, ApiError } from '../api/client'
import type { ChiaveDelCliente, ChiaveDelClienteIn, StatoCifratura } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, ErrorBanner, formatDateTime, Spinner } from './common'

const FORM_VUOTO: ChiaveDelClienteIn = {
  provider: 'vault',
  config: { endpoint: '', namespace: '', mount: 'transit', key: '', verify_tls: true, modo: 'cloud', appliance_id: '' },
  secret: '',
}

/**
 * «Porta la tua chiave» (BYOK, ADR-0028): the tenant's data keys wrapped by
 * a key in the customer's own KMS — HashiCorp Vault Transit today — and
 * opened by asking it. From the Control Plane, or through one of the
 * customer's probes when Vault lives only on his network. The customer
 * revokes by taking the token's policy away: from then on his secrets do
 * not open, and that is the point.
 */
function PortaLaTuaChiave({ d, onReload }: { d: StatoCifratura; onReload: () => Promise<unknown> | void }) {
  const kek = d.kek
  const [apri, setApri] = useState(false)
  const [form, setForm] = useState<ChiaveDelClienteIn>(FORM_VUOTO)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const [conferma, setConferma] = useState<'adotta' | 'togli' | null>(null)
  const sonde = useAsync(() => api.probes(), [])
  useEffect(() => {
    if (kek) setForm((f) => ({ ...f, provider: kek.provider, config: { ...FORM_VUOTO.config, ...kek.config }, secret: '' }))
  }, [kek])
  const cambia = (parte: Partial<ChiaveDelCliente['config']>) => setForm((f) => ({ ...f, config: { ...f.config, ...parte } }))
  const corpo = (): ChiaveDelClienteIn => ({
    provider: form.provider,
    config: { ...form.config, appliance_id: form.config.modo === 'probe' ? form.config.appliance_id : undefined },
    secret: form.secret || null,
  })

  async function esegui(azione: () => Promise<unknown>, dopo?: () => void) {
    setErrore(null)
    setLavoro(true)
    try {
      await azione()
      dopo?.()
    } catch (err) {
      setErrore(err instanceof ApiError && err.code === 'rotation_in_progress'
        ? new Error(t('La rotazione precedente è ancora in corso: aspetta che tutti i segreti siano ricifrati.'))
        : err)
    } finally {
      setLavoro(false)
    }
  }
  const prova = () => esegui(async () => {
    const r = await api.encryptionKekTest(corpo())
    setEsito(t('Vault risponde: cifra e decifra con la chiave «{ref}».', { ref: r.ref ?? form.config.key ?? '' }))
  })
  const adotta = () => esegui(() => api.encryptionKekAdopt(corpo()), () => { setApri(false); setConferma(null); setEsito(null); void onReload() })
  const togli = () => esegui(() => api.encryptionKekDrop(), () => { setConferma(null); void onReload() })

  const modulo = (
    <div className="form-grid chiave__modulo">
      <label className="field span-2">
        <span className="field__label">{t('KMS')}</span>
        <select value={form.provider} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}>
          <option value="vault">{t('HashiCorp Vault (Transit)')}</option>
        </select>
        <span className="field__hint">{t('AWS KMS, Azure Key Vault e GCP KMS: con lo stesso contratto, quando servono.')}</span>
      </label>
      <label className="field span-4">
        <span className="field__label">{t('Indirizzo di Vault')}</span>
        <input type="url" value={form.config.endpoint ?? ''} placeholder="https://vault.azienda.it:8200" onChange={(e) => cambia({ endpoint: e.target.value })} />
      </label>
      <label className="field span-2">
        <span className="field__label">{t('Namespace (facoltativo)')}</span>
        <input type="text" value={form.config.namespace ?? ''} onChange={(e) => cambia({ namespace: e.target.value })} />
      </label>
      <label className="field span-2">
        <span className="field__label">{t('Motore Transit')}</span>
        <input type="text" value={form.config.mount ?? 'transit'} onChange={(e) => cambia({ mount: e.target.value })} />
      </label>
      <label className="field span-2">
        <span className="field__label">{t('Nome della chiave')}</span>
        <input type="text" value={form.config.key ?? ''} placeholder="censo" onChange={(e) => cambia({ key: e.target.value })} />
      </label>
      <label className="field span-3">
        <span className="field__label">{t('Token di Vault')}</span>
        <input type="password" value={form.secret ?? ''} autoComplete="off"
               placeholder={kek ? t('lascia vuoto per tenere quello salvato') : ''}
               onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))} />
        <span className="field__hint">{t('Una policy con «encrypt» e «decrypt» su quella chiave, niente altro.')}</span>
      </label>
      <label className="field span-3">
        <span className="field__label">{t('Da dove si chiama Vault')}</span>
        <select value={form.config.modo ?? 'cloud'} onChange={(e) => cambia({ modo: e.target.value })}>
          <option value="cloud">{t('dal Control Plane (Vault raggiungibile da Internet)')}</option>
          <option value="probe">{t('attraverso una sonda (Vault solo nella tua rete)')}</option>
        </select>
      </label>
      {form.config.modo === 'probe' && (
        <label className="field span-3">
          <span className="field__label">{t('Sonda')}</span>
          <select value={form.config.appliance_id ?? ''} onChange={(e) => cambia({ appliance_id: e.target.value })}>
            <option value="">—</option>
            {(sonde.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      )}
      <label className="field span-3 field--inline">
        <input type="checkbox" checked={form.config.verify_tls !== false} onChange={(e) => cambia({ verify_tls: e.target.checked })} />
        <span>{t('Verifica il certificato TLS di Vault')}</span>
      </label>
      <div className="field span-6 chiave__azioni">
        <button type="button" className="secondary" onClick={prova} disabled={lavoro}>{t('Prova la connessione')}</button>
        {conferma !== 'adotta' ? (
          <button type="button" onClick={() => setConferma('adotta')} disabled={lavoro}>{kek ? t('Salva e ri-avvolgi con questa chiave') : t('Adotta questa chiave')}</button>
        ) : (
          <span className="chiave__conferma">
            {t('Da ora le chiavi dei tuoi segreti sono avvolte dalla tua chiave: i segreti esistenti vengono ricifrati in background e le versioni che la piattaforma poteva aprire spariscono a fine ricifratura. Se la chiave non risponde, discovery e query live si fermano.')}{' '}
            <button type="button" onClick={adotta} disabled={lavoro}>{lavoro ? t('Un momento…') : t('Conferma')}</button>{' '}
            <button type="button" className="secondary" onClick={() => setConferma(null)} disabled={lavoro}>{t('Annulla')}</button>
          </span>
        )}
        {kek && <button type="button" className="linklike" onClick={() => { setApri(false); setErrore(null); setEsito(null) }}>{t('chiudi')}</button>}
      </div>
      {esito && <p className="field span-6 chiave__esito">{esito}</p>}
    </div>
  )

  return (
    <div className="chiave__sezione">
      <h4>{t('La tua chiave (BYOK)')}</h4>
      <ErrorBanner error={errore} />
      {!kek && !d.byok_licensed && (
        <p className="muted">{t('Non compreso nella licenza')}</p>
      )}
      {kek && (
        <>
          <div className="chiave__stato">
            <div>
              <span className="chiave__etichetta">{t('KMS')}</span>
              <strong className="chiave__valore chiave__valore--piccolo">{kek.provider === 'vault' ? t('HashiCorp Vault (Transit)') : kek.provider}</strong>
            </div>
            <div>
              <span className="chiave__etichetta">{t('Chiave')}</span>
              <strong className="chiave__valore chiave__valore--piccolo mono" title={kek.ref}>{kek.config.key ?? kek.ref}</strong>
            </div>
            <div>
              <span className="chiave__etichetta">{t('Ultimo uso')}</span>
              <strong className="chiave__valore chiave__valore--piccolo">{kek.last_unwrap_at ? formatDateTime(kek.last_unwrap_at) : '—'}</strong>
            </div>
            <div>
              <span className="chiave__etichetta">{t('Stato')}</span>
              <strong className="chiave__valore chiave__valore--piccolo">
                {kek.status === 'retiring' ? t('in ritiro') : kek.last_error ? t('non disponibile') : t('attiva')}
              </strong>
            </div>
          </div>
          {kek.last_error && <p className="eagle-motivo">{kek.last_error}</p>}
          <p className="muted">
            {kek.status === 'retiring'
              ? t('Stai tornando alla chiave della piattaforma: la tua chiave resta finché le versioni che ha avvolto non sono ricifrate.')
              : kek.platform_versions > 0
                ? t('{n} versioni della chiave dati le può ancora aprire la piattaforma: spariscono a ricifratura finita.', { n: kek.platform_versions })
                : t('Solo la tua chiave apre i segreti di questo tenant. Da {quando}.', { quando: kek.since ? formatDateTime(kek.since) : '—' })}
            {' '}{kek.config.modo === 'probe' ? t('Vault si chiama attraverso una tua sonda.') : t('Vault si chiama dal Control Plane.')}
          </p>
          {!apri && (
            <p className="chiave__azioni">
              <button type="button" className="secondary" onClick={() => setApri(true)} disabled={lavoro}>{t('Cambia impostazioni o token')}</button>{' '}
              {kek.status !== 'retiring' && (conferma !== 'togli' ? (
                <button type="button" className="secondary" onClick={() => setConferma('togli')} disabled={lavoro}>{t('Torna alla chiave della piattaforma')}</button>
              ) : (
                <span className="chiave__conferma">
                  {t('Le chiavi dei segreti tornano sotto la chiave della piattaforma; serve che Vault risponda ancora per ricifrare. Confermi?')}{' '}
                  <button type="button" onClick={togli} disabled={lavoro}>{t('Conferma')}</button>{' '}
                  <button type="button" className="secondary" onClick={() => setConferma(null)} disabled={lavoro}>{t('Annulla')}</button>
                </span>
              ))}
            </p>
          )}
        </>
      )}
      {d.byok_licensed && (!kek || apri) && modulo}
      {(kek || d.byok_licensed) && (
        <p className="muted eagle-piccolo">
          {t('Vale per i segreti (credenziali, connettori, token), non per l’inventario del database condiviso; durante l’uso i segreti stanno in memoria del Control Plane. Se il KMS non risponde, discovery e query live di questo tenant si fermano con «chiave non disponibile».')}
        </p>
      )}
    </div>
  )
}

/**
 * La chiave con cui sono cifrati i segreti del tenant — credenziali di
 * discovery, connettori — e la sua rotazione, per chi amministra il tenant.
 *
 * La chiave è del cliente, ed è lui a sapere quando va ruotata (un
 * amministratore uscito, un sospetto, una politica interna): prima poteva farlo
 * solo il fornitore dal piano di amministrazione. Ruotare non ferma niente: i
 * segreti nuovi si cifrano subito con la chiave nuova, gli esistenti vengono
 * ricifrati in background, e qui si vede l'avanzamento. Vedi
 * control-plane/app/api/v1/encryption.py e ADR-0021.
 */
export function ChiaveSegreti() {
  const stato = useAsync(() => api.encryptionStatus(), [])
  const [errore, setErrore] = useState<unknown>(null)
  const [conferma, setConferma] = useState(false)
  const [lavoro, setLavoro] = useState(false)
  const d = stato.data
  const inCorso = (d?.to_migrate ?? 0) > 0 && d?.active_version !== null

  // Mentre la ricifratura va avanti, si riguarda ogni 5 secondi: l'avanzamento
  // si vede muoversi e il pulsante torna disponibile da solo.
  useEffect(() => {
    if (!inCorso) return
    const timer = window.setInterval(() => void stato.reload(), 5000)
    return () => window.clearInterval(timer)
  }, [inCorso]) // eslint-disable-line react-hooks/exhaustive-deps

  async function ruota() {
    setErrore(null)
    setLavoro(true)
    try {
      await api.rotateEncryptionKey()
      setConferma(false)
      await stato.reload()
    } catch (err) {
      setErrore(
        err instanceof ApiError && err.code === 'rotation_in_progress'
          ? new Error(t('La rotazione precedente è ancora in corso: aspetta che tutti i segreti siano ricifrati.'))
          : err,
      )
    } finally {
      setLavoro(false)
    }
  }

  const fatti = d ? d.secrets - d.to_migrate : 0
  const percentuale = d && d.secrets > 0 ? Math.round((fatti / d.secrets) * 100) : 100

  return (
    <Card title={t('Chiave dei segreti')}>
      <p style={{ marginTop: 0 }}>
        {t('Le credenziali di discovery e dei connettori del tenant sono cifrate con una chiave privata dedicata. Il rinnovo della chiave genera una nuova chiave di cifratura, utilizzata immediatamente per le nuove credenziali. Le credenziali già archiviate vengono ricifrate automaticamente in background, senza interruzioni del servizio o perdita di accesso.')}
      </p>
      <ErrorBanner error={errore ?? stato.error} />
      {stato.loading && !d ? (
        <Spinner />
      ) : d ? (
        <>
          <div className="chiave__stato">
            <div>
              <span className="chiave__etichetta">{t('Versione attiva')}</span>
              <strong className="chiave__valore">{d.active_version ?? '—'}</strong>
            </div>
            <div>
              <span className="chiave__etichetta">{t('Segreti cifrati')}</span>
              <strong className="chiave__valore">{d.secrets}</strong>
            </div>
            <div>
              <span className="chiave__etichetta">{t('Ultima rotazione')}</span>
              <strong className="chiave__valore chiave__valore--piccolo">
                {d.versions[0]?.created_at ? formatDateTime(d.versions[0].created_at) : '—'}
              </strong>
            </div>
          </div>

          {inCorso && (
            <div className="chiave__avanzamento" role="status">
              <div className="chiave__barra">
                <span style={{ width: `${percentuale}%` }} />
              </div>
              <span>
                {t('Ricifratura in corso: {fatti} di {totale} segreti sulla chiave nuova', {
                  fatti,
                  totale: d.secrets,
                })}
              </span>
            </div>
          )}

          {!conferma ? (
            <button type="button" onClick={() => setConferma(true)} disabled={inCorso || lavoro}>
              {t('Ruota la chiave')}
            </button>
          ) : (
            <div className="chiave__conferma">
              <p>
                {t('Viene creata la versione {n} della chiave. Nessun servizio si ferma e nessuna credenziale va reinserita; la chiave precedente resta finché serve a leggere i segreti non ancora ricifrati. L’operazione resta nell’audit.', {
                  n: (d.active_version ?? 0) + 1,
                })}
              </p>
              <button type="button" onClick={ruota} disabled={lavoro}>
                {lavoro ? t('Rotazione…') : t('Conferma la rotazione')}
              </button>{' '}
              <button type="button" className="secondary" onClick={() => setConferma(false)} disabled={lavoro}>
                {t('Annulla')}
              </button>
            </div>
          )}

          {d.versions.length > 1 && (
            <details className="chiave__storia">
              <summary>{t('Storia delle chiavi ({n})', { n: d.versions.length })}</summary>
              <ul>
                {d.versions.map((v) => (
                  <li key={v.version}>
                    {t('Versione {n}', { n: v.version })} —{' '}
                    {v.created_at ? formatDateTime(v.created_at) : '—'}
                    {v.version === d.active_version && <span className="chip">{t('attiva')}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <PortaLaTuaChiave d={d} onReload={() => stato.reload()} />
        </>
      ) : null}
    </Card>
  )
}
